// @file Composable for generating whiteboard connection URI
import environment from '@@/bits/environment'
import { captureFetchException } from '@@/bits/error_tracker'
import { isDebugMode } from '@@/bits/flip'
import window from '@@/bits/global'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import type { VersionedFetchOptions } from '@@/surface/api/fetchable_object'
import { WhiteboardAccessTokenApi } from '@@/surface/api/whiteboard_access_token'
import { WhiteboardAiAccessTokenApi } from '@@/surface/api/whiteboard_ai_access_token'
import type { FetchOptionQueryHash } from '@padlet/fetch/src/types'
import { createSharedComposable } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

// Change this value if you want to test with a remote server, which is needed for functionality like remakes.
// Note that the storage is shared so it may clash with other local envs with the same wall id.
const USE_REMOTE_DEV_WORKER = false

const HOST: string = (() => {
  const searchParams = new URLSearchParams(window.location.search)
  const whiteboardServerHostFromSearchParams = searchParams.get('whiteboard_server_host')

  switch (environment) {
    case 'production': {
      if (isDebugMode && whiteboardServerHostFromSearchParams != null) {
        return whiteboardServerHostFromSearchParams
      }
      const isBeta = window.location.hostname.endsWith('beta.padlet.com')
      return isBeta ? 'https://whiteboard-server-beta.padlet.com' : 'https://whiteboard-server.padlet.com'
    }
    case 'staging': {
      // We don't have a specific server for staging right now
      return whiteboardServerHostFromSearchParams ?? ''
    }
    case 'e2e_test':
    case 'e2e_ci': {
      return 'https://whiteboard-server-e2e.padlet.test'
    }
    case 'development':
    default:
      return USE_REMOTE_DEV_WORKER
        ? 'https://whiteboard_server_dev.padlet.workers.dev'
        : 'https://whiteboard-server.padletdev.com'
  }
})()

export const useWhiteboardConnectionUri = createSharedComposable(
  (): {
    prefetchAccessTokens: () => Promise<void>
    getConnectionUri: () => Promise<string>
    getAiGenerateUri: () => Promise<string>
    getAiStreamUri: () => Promise<string>
  } => {
    const { wallId, wallHashidWithoutPrefix } = storeToRefs(useSurfaceStore())
    const { isFrameBreakout, breakoutFrameId } = storeToRefs(useWhiteboardStore())

    const prefetchedAccessToken = ref<string | null>(null)
    const prefetchedAiAccessToken = ref<string | null>(null)

    const fetchOptions = computed(() => {
      const options: VersionedFetchOptions & { query?: FetchOptionQueryHash } = {
        apiVersion: 9,
      }

      // If we're in a breakout room, we pass the current frame ID as the breakout_room parameter
      if (isFrameBreakout.value && breakoutFrameId.value != null) {
        options.query = {
          breakout_room: breakoutFrameId.value,
        }
      }

      return options
    })

    const prefetchAccessTokens = async (): Promise<void> => {
      try {
        prefetchedAccessToken.value = await WhiteboardAccessTokenApi.fetch(
          wallHashidWithoutPrefix.value,
          fetchOptions.value,
        )
        prefetchedAiAccessToken.value = await WhiteboardAiAccessTokenApi.fetch(
          wallHashidWithoutPrefix.value,
          fetchOptions.value,
        )
      } catch (e) {
        captureFetchException(e, { source: 'useWhiteboardConnectionUri#prefetchAccessTokens' })
      }
    }

    const getConnectionUri = async (): Promise<string> => {
      const url = new URL(`${HOST}/connect/${wallId.value}`)

      const accessToken =
        prefetchedAccessToken.value ??
        (await WhiteboardAccessTokenApi.fetch(wallHashidWithoutPrefix.value, fetchOptions.value))

      // After one call, the prefetched token should not be valid anymore so we unset it.
      // The next time we call this function, we should get a new token.
      prefetchedAccessToken.value = null

      url.searchParams.set('accessToken', accessToken)
      return url.toString()
    }

    const getAiGenerateUri = async (): Promise<string> => {
      const url = new URL(`${HOST}/ai-generate`)
      const accessToken =
        prefetchedAiAccessToken.value ??
        (await WhiteboardAiAccessTokenApi.fetch(wallHashidWithoutPrefix.value, fetchOptions.value))

      url.searchParams.set('accessToken', accessToken)
      return url.toString()
    }

    const getAiStreamUri = async (): Promise<string> => {
      const url = new URL(`${HOST}/ai-stream`)
      const accessToken =
        prefetchedAiAccessToken.value ??
        (await WhiteboardAiAccessTokenApi.fetch(wallHashidWithoutPrefix.value, fetchOptions.value))

      url.searchParams.set('accessToken', accessToken)
      return url.toString()
    }

    return {
      prefetchAccessTokens,
      getConnectionUri,
      getAiGenerateUri,
      getAiStreamUri,
    }
  },
)
